// Session-based chat storage to persist messages across component mounts
// Messages are cleared on sign out, project change, or session close

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  copied?: boolean;
}

interface ChatStorage {
  [key: string]: Message[];
}

// Use localStorage for persistence until export (survives tab close)
// Messages cleared on sign out, project change, or successful export
const STORAGE_KEY = 'verithos_chat_history';

const getStorage = (): ChatStorage => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Restore Date objects
      Object.keys(parsed).forEach(key => {
        parsed[key] = parsed[key].map((m: Message & { timestamp: string }) => ({
          ...m,
          timestamp: new Date(m.timestamp),
        }));
      });
      return parsed;
    }
  } catch (e) {
    console.error('Failed to parse chat storage:', e);
  }
  return {};
};

const saveStorage = (storage: ChatStorage) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
  } catch (e) {
    console.error('Failed to save chat storage:', e);
  }
};

export const chatStorage = {
  getMessages: (chatId: string): Message[] => {
    const storage = getStorage();
    return storage[chatId] || [];
  },
  
  setMessages: (chatId: string, messages: Message[]) => {
    const storage = getStorage();
    storage[chatId] = messages;
    saveStorage(storage);
  },
  
  clearChat: (chatId: string) => {
    const storage = getStorage();
    delete storage[chatId];
    saveStorage(storage);
  },
  
  clearAll: () => {
    localStorage.removeItem(STORAGE_KEY);
  },
  
  // Clear after successful export
  clearAfterExport: (chatId: string) => {
    const storage = getStorage();
    delete storage[chatId];
    saveStorage(storage);
  },
  
  // Clear chats related to a specific project
  clearForProject: (projectId: string) => {
    const storage = getStorage();
    Object.keys(storage).forEach(key => {
      if (key.includes(projectId)) {
        delete storage[key];
      }
    });
    saveStorage(storage);
  },
};
